/**
 * Generates a proper YAML-compatible step name from display name and type
 * @param {string} stepName - The display name of the step
 * @param {string} stepType - The type of the step
 * @returns {string} - YAML-compatible step name in snake_case
 */
export const generateStepName = (stepName, stepType) => {
  // Convert to snake_case and ensure uniqueness
  const baseName = stepName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '');
  
  return baseName || `${stepType.toLowerCase()}_step`;
};

/**
 * Generates a unique internal ID for React components
 * @param {string} prefix - Prefix for the ID (default: 'step')
 * @returns {string} - Unique internal ID
 */
export const generateInternalId = (prefix = 'step') => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generates a unique workflow ID
 * @returns {string} - Unique workflow ID
 */
export const generateWorkflowId = () => {
  return `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Creates a mapping of various step identifiers to internal IDs
 * Used for resolving action routing references during import
 * @param {Array} steps - Array of step objects
 * @returns {Map} - Map of identifiers to internal IDs
 */
export const createStepIdMapping = (steps) => {
  const stepIdMap = new Map();
  const stepNameMap = new Map();
  
  steps.forEach((step, index) => {
    const internalId = step.id;
    const stepName = step.name || `Step ${index + 1}`;
    const yamlStepName = step.stepName || generateStepName(stepName, step.type);
    
    // Map various ways this step might be referenced
    if (step.originalId) {
      stepIdMap.set(step.originalId, internalId);
    }
    
    // Case-insensitive name mapping
    stepNameMap.set(stepName.toLowerCase(), internalId);
    stepNameMap.set(yamlStepName, internalId);
    
    // Common name variations for action routing
    const nameVariations = [
      stepName.toLowerCase().replace(/\s+/g, '_'),
      stepName.toLowerCase().replace(/\s+/g, ''),
      yamlStepName,
      `step_${index + 1}`,
      `step${index + 1}`,
      step.originalId || ''
    ];
    
    nameVariations.forEach(variation => {
      if (variation && variation.trim()) {
        stepIdMap.set(variation, internalId);
      }
    });
  });
  
  return { stepIdMap, stepNameMap };
};

/**
 * Resolves action routing target to internal step ID
 * @param {string} target - The target reference from action routing
 * @param {Map} stepIdMap - Map of identifiers to internal IDs
 * @param {Map} stepNameMap - Map of step names to internal IDs
 * @returns {string|null} - Resolved internal ID or null if not found
 */
export const resolveActionTarget = (target, stepIdMap, stepNameMap) => {
  if (target === 'end' || target === 'error') {
    return target;
  }
  
  // Try exact ID match first
  if (stepIdMap.has(target)) {
    return stepIdMap.get(target);
  }
  
  // Try case-insensitive name match
  if (stepNameMap.has(target.toLowerCase())) {
    return stepNameMap.get(target.toLowerCase());
  }
  
  // Try variations
  const variations = [
    target.toLowerCase().replace(/\s+/g, '_'),
    target.toLowerCase().replace(/\s+/g, ''),
    target
  ];
  
  for (const variation of variations) {
    if (stepIdMap.has(variation)) {
      return stepIdMap.get(variation);
    }
  }
  
  return null; // Target not found
};

/**
 * Validates and ensures step name uniqueness within a workflow
 * @param {string} proposedName - The proposed step name
 * @param {Array} existingSteps - Array of existing steps
 * @param {string} currentStepId - ID of current step (to exclude from uniqueness check)
 * @returns {string} - Unique step name
 */
export const ensureUniqueStepName = (proposedName, existingSteps, currentStepId = null) => {
  const existingNames = existingSteps
    .filter(step => step.id !== currentStepId)
    .map(step => step.stepName || generateStepName(step.name, step.type));
  
  let uniqueName = proposedName;
  let counter = 1;
  
  while (existingNames.includes(uniqueName)) {
    uniqueName = `${proposedName}_${counter}`;
    counter++;
  }
  
  return uniqueName;
};

/**
 * Updates step names when display name changes
 * @param {Object} step - The step object
 * @param {string} newDisplayName - New display name
 * @param {Array} allSteps - All steps in workflow for uniqueness check
 * @returns {Object} - Updated step object
 */
export const updateStepNames = (step, newDisplayName, allSteps = []) => {
  const newStepName = generateStepName(newDisplayName, step.type);
  const uniqueStepName = ensureUniqueStepName(newStepName, allSteps, step.id);
  
  return {
    ...step,
    name: newDisplayName,
    stepName: uniqueStepName
  };
};